import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '@/lib/supabase';
import { errorTracker } from '@/lib/monitoring';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper function to get current user profile
  const getCurrentUserProfile = async (authUser) => {
    console.log('Fetching profile for user_id:', authUser.id);

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', authUser.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching profile:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      throw error;
    }

    console.log('Profile data received:', data);

    if (!data) {
      console.warn('No profile found for user:', authUser.id);
      console.warn('User email:', authUser.email);
    }

    return data;
  };

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('Getting initial session...');
        const { data: { session } } = await supabase.auth.getSession();
        console.log('Session retrieved:', session);

        if (session?.user) {
          console.log('User found in session, loading profile...');
          await loadUserProfile(session.user);
        } else {
          console.log('No user in session');
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        errorTracker.logError(error, { operation: 'auth_initialization' });
      }
      setIsLoading(false);
      setIsInitialized(true);
      console.log('Auth initialization complete');
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.email);

      // Use an async IIFE to avoid deadlocks
      (async () => {
        try {
          if (event === 'SIGNED_IN' && session?.user) {
            console.log('SIGNED_IN event detected, loading profile...');
            await loadUserProfile(session.user);
          } else if (event === 'SIGNED_OUT') {
            console.log('SIGNED_OUT event detected');
            setUser(null);
            setProfile(null);
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            console.log('TOKEN_REFRESHED event detected');
            // Don't reload profile on token refresh if already loaded
            if (!profile) {
              await loadUserProfile(session.user);
            }
          }
          setIsLoading(false);
        } catch (error) {
          console.error('Error in auth state change handler:', error);
          setIsLoading(false);
        }
      })();
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfile = async (authUser) => {
    try {
      console.log('=== Loading profile for user ===');
      console.log('User ID:', authUser.id);
      console.log('User Email:', authUser.email);
      setUser(authUser);

      // Try to get existing profile
      const existingProfile = await getCurrentUserProfile(authUser);
      console.log('Profile fetch completed:', existingProfile);

      if (existingProfile) {
        setProfile(existingProfile);
        console.log('Profile set successfully:', {
          id: existingProfile.id,
          email: existingProfile.email,
          role: existingProfile.role,
          status: existingProfile.status
        });

        // Update last login (using profile id, not user_id)
        try {
          const { error: updateError } = await supabase
            .from('user_profiles')
            .update({ last_login_at: new Date().toISOString() })
            .eq('id', existingProfile.id);

          if (updateError) {
            console.warn('Failed to update last_login_at:', updateError);
          } else {
            console.log('Last login timestamp updated successfully');
          }
        } catch (updateErr) {
          console.warn('Non-critical error updating last_login_at:', updateErr);
        }
      } else {
        console.error('NO PROFILE FOUND! This should not happen for existing users.');
        console.error('User exists in auth.users but not in user_profiles table');
        setProfile(null);
      }

      console.log('=== Profile loading complete ===');
    } catch (error) {
      console.error('=== ERROR loading user profile ===');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      errorTracker.logError(error, { operation: 'loadUserProfile', userId: authUser.id });
      setProfile(null);
    }
  };

  const signIn = async (email, password) => {
    try {
      console.log('=== Sign in attempt ===');
      console.log('Email:', email);
      setIsLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password
      });

      if (error) {
        console.error('Sign in error:', error);
        errorTracker.logError(error, { operation: 'signIn', email });
        setIsLoading(false);
        throw error;
      }

      console.log('Sign in successful! User:', data.user?.email);
      console.log('Session:', data.session ? 'exists' : 'missing');

      // Note: Profile loading will be handled by onAuthStateChange
      return data;
    } catch (error) {
      console.error('=== Sign in failed ===');
      setIsLoading(false);
      throw error;
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) {
        errorTracker.logError(error, { operation: 'signUp', email });
        throw error;
      }
      
      return data;
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setProfile(null);
  };

  const updateProfile = async (updates) => {
    if (!profile) throw new Error('No profile loaded');
    
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', profile.id)
      .select('*, university:universities(*)')
      .single();

    if (error) throw error;
    
    setProfile(data);
    return data;
  };

  const hasPermission = (action, resource = null) => {
    if (!profile) return false;
    
    // Super admin has all permissions
    if (profile.role === 'super_admin') return true;
    
    // University admin has permissions within their university
    if (profile.role === 'university_admin') {
      return resource ? resource.university_id === profile.university_id : true;
    }
    
    // Agents have limited permissions
    if (profile.role === 'agent' || profile.role === 'sub_agent') {
      if (action === 'read') return true;
      if (action === 'write' && resource) {
        return resource.agent_id === profile.id || resource.university_id === profile.university_id;
      }
    }
    
    return false;
  };

  const value = {
    user,
    profile,
    isLoading,
    isInitialized,
    signIn,
    signUp,
    signOut,
    updateProfile,
    hasPermission,
    isAuthenticated: !!user,
    isSuperAdmin: profile?.role === 'super_admin',
    isUniversityAdmin: profile?.role === 'university_admin' || profile?.role === 'university_staff',
    isAgent: profile?.role === 'agent' || profile?.role === 'sub_agent'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};